import type { ListActionRef } from "../../../../../../../shared/frontend/list/listColumn";
import { useFinanceOperation } from "../../../app/operations/useFinanceOperation";
import { contractRequestChannelLabelKey } from "../../../components/contracts/contractRequestChannel";
import { DeleteOutlined,EditOutlined,PlusOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";

import { Button,Space,Tag } from "../../../../../../../shared/frontend/ui";
import type { ModalFuncProps } from "../../../../../../../shared/frontend/ui";
import dayjs from "dayjs";
import React from "react";
import type { ApiClient } from "../../../app/api/client";
import {
  REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME,
  REST_PERIOD_BILLING_SCHEDULE_SHIFT_DUE_DATES,
} from "../../../app/constants/restPeriodBillingScheduleMode";
import type { LocalizedText } from "../../../app/i18n/i18n";
import { formatDateRange,formatMoney } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import type { ColumnDefinition } from "../../../app/utils/useProTableColumnEditor";
import type { RestPeriodRow,RestPeriodApiRow } from "../../../components/contracts/restPeriodRows";
import { restPeriodFeeAmountUnit,restPeriodFeeUnitText } from "../../../components/contracts/restPeriodFeeUtils";
import { normalizeRestPeriodRows } from "../../../components/contracts/restPeriodRows";
import { compareDateOnlyDesc,compareTextDesc } from "./personDetailUtils";
import { invalidatePersonContractData } from "./personDetailQueryKeys";
import type { ProColumnsWithMeasure } from "../../../components/SettingsListPage";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

const CONFIRMATION_SORT_ORDER = { unknown: 0, open: 1, sent: 2 } as const;
const REST_PERIODS_PAGE_SIZE = 10;

const USE_PERSON_REST_PERIODS_CONTROLLER_COLUMN_WIDTH = {
  period: 220,
  contract: 240,
  template: 220,
  reason: 180,
  channel: 120,
  confirmation: 180,
  monthlyFee: 140,
  weeksCount: 120,
  options: 300,
  actions: 160
} as const;

type RestPeriodPrefill = {
  contractPackageId?: string;
  startDate?: string;
  endDate?: string;
  note?: string;
};

type UsePersonRestPeriodsControllerArgs = {
  api: Pick<ApiClient, "get" | "del">;
  personId: string;
  canWriteRestPeriods: boolean;
  text: (value: LocalizedText) => string;
  units: UnitsSettings;
  message: {
    success(content: React.ReactNode): void;
  };
  modal: {
    confirm(config: ModalFuncProps): unknown;
  };
  onMemberAppRequestClosed: () => void;
};

function compareRestPeriodsNewestFirst(a: RestPeriodRow, b: RestPeriodRow): number {
  return (
    compareDateOnlyDesc(a.startDate, b.startDate)
    || compareDateOnlyDesc(a.endDate, b.endDate)
    || compareTextDesc(a.createdAt, b.createdAt)
    || compareTextDesc(a.id, b.id)
  );
}

export function usePersonRestPeriodsController({
  api,
  personId,
  canWriteRestPeriods,
  text,
  units,
  message,
  modal,
  onMemberAppRequestClosed
}: UsePersonRestPeriodsControllerArgs) {
  const queryClient = useQueryClient();
  const financeOperation = useFinanceOperation(personId);
  const actionRef = React.useRef<ListActionRef>(null);
  const [open, setOpen] = React.useState(false);
  const [edit, setEdit] = React.useState<RestPeriodRow | null>(null);
  const [prefill, setPrefill] = React.useState<RestPeriodPrefill | null>(null);

  const reloadDependents = React.useCallback(() => {
    void invalidatePersonContractData(queryClient, personId);
  }, [personId, queryClient]);

  const openCreate = React.useCallback(() => {
    setEdit(null);
    setPrefill(null);
    onMemberAppRequestClosed();
    setOpen(true);
  }, [onMemberAppRequestClosed]);

  const openEdit = React.useCallback((row: RestPeriodRow) => {
    if (row.isCancelled) return;
    setEdit(row);
    setPrefill(null);
    onMemberAppRequestClosed();
    setOpen(true);
  }, [onMemberAppRequestClosed]);

  const close = React.useCallback(() => {
    setOpen(false);
    setEdit(null);
    setPrefill(null);
    onMemberAppRequestClosed();
  }, [onMemberAppRequestClosed]);

  const deleteRow = React.useCallback(
    async (row: RestPeriodRow) => {
      const ok = await new Promise<boolean>((resolve) => {
        modal.confirm({
          title: text("Delete rest period"),
          content: text("Really delete?"),
          okText: text("delete"),
          okButtonProps: { danger: true },
          autoFocusButton: "cancel",
          cancelText: text("cancel"),
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });
      if (!ok) return;
      await financeOperation.execute(async (committed) => {
        await api.del(`/api/v1/rest-periods/${encodeURIComponent(row.id)}`);
        committed();
        message.success(text("Deleted."));
      });
    },
    [api, message, modal, text, financeOperation]
  );

  React.useEffect(() => {
    const handleRestPeriodsChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ personId?: string | null }>).detail;
      if (detail?.personId && String(detail.personId) !== String(personId)) return;
      reloadDependents();
    };
    window.addEventListener("person-rest-periods-changed", handleRestPeriodsChanged);
    return () => window.removeEventListener("person-rest-periods-changed", handleRestPeriodsChanged);
  }, [personId, reloadDependents]);

  const columnDefinitions = React.useMemo<ColumnDefinition[]>(
    () => [
      { key: "period", label: text("Period"), defaultVisible: true },
      { key: "status", label: text("Status"), defaultVisible: true },
      { key: "contract", label: text("Contract"), defaultVisible: true },
      { key: "template", label: text("Template"), defaultVisible: true },
      { key: "reason", label: text("Reason"), defaultVisible: true },
      { key: "channel", label: text("Request channel"), defaultVisible: false },
      { key: "confirmation", label: text("Confirmation"), defaultVisible: true },
      { key: "monthlyFee", label: text("Fee"), defaultVisible: true },
      { key: "weeksCount", label: text("Installments"), defaultVisible: false },
      { key: "options", label: text("Options"), defaultVisible: true }
    ],
    [text]
  );

  const allColumnsMap = React.useMemo<Record<string, ProColumnsWithMeasure<RestPeriodRow>>>(() => ({
    status: {
      title: text("Status"),
      dataIndex: "isCancelled",
      key: "status",
      render: (_, row) => row.isCancelled
        ? <Tag color="red" title={row.note || undefined}>{text("cancelled")}</Tag>
        : <Tag>{text("active")}</Tag>
    },
    period: {
      title: text("Period"),
      dataIndex: "startDate",
      key: "period",
      width: USE_PERSON_REST_PERIODS_CONTROLLER_COLUMN_WIDTH.period,
      sorter: (a, b) => {
        const aDate = dayjs(String(a.startDate || "").slice(0, ISO_DATE_LENGTH));
        const bDate = dayjs(String(b.startDate || "").slice(0, ISO_DATE_LENGTH));
        if (!aDate.isValid() && !bDate.isValid()) return 0;
        if (!aDate.isValid()) return 1;
        if (!bDate.isValid()) return -1;
        return aDate.isBefore(bDate) ? -1 : aDate.isAfter(bDate) ? 1 : 0;
      },
      render: (_, row) => formatDateRange(String(row.startDate || "").slice(0, ISO_DATE_LENGTH), String(row.endDate || "").slice(0, ISO_DATE_LENGTH), units)
    },
    contract: {
      title: text("Contract"),
      dataIndex: "contractPlanName",
      key: "contract",
      width: USE_PERSON_REST_PERIODS_CONTROLLER_COLUMN_WIDTH.contract,
      sorter: (a, b) => {
        const aName = String(a.contractPlanName || "").trim();
        const bName = String(b.contractPlanName || "").trim();
        return aName.localeCompare(bName);
      },
      render: (_, row) => String(row.contractPlanName || "").trim() || row.contractPackageId
    },
    template: {
      title: text("Template"),
      dataIndex: "restPeriodTemplateName",
      key: "template",
      width: USE_PERSON_REST_PERIODS_CONTROLLER_COLUMN_WIDTH.template,
      sorter: (a, b) => {
        const aName = String(a.restPeriodTemplateName || "").trim();
        const bName = String(b.restPeriodTemplateName || "").trim();
        return aName.localeCompare(bName);
      },
      render: (_, row) => row.restPeriodTemplateName || "—"
    },
    reason: {
      title: text("Reason"),
      dataIndex: "restPeriodReasonName",
      key: "reason",
      width: USE_PERSON_REST_PERIODS_CONTROLLER_COLUMN_WIDTH.reason,
      sorter: (a, b) => {
        const aName = String(a.restPeriodReasonName || "").trim();
        const bName = String(b.restPeriodReasonName || "").trim();
        return aName.localeCompare(bName);
      },
      render: (_, row) => row.restPeriodReasonName || "—"
    },
    channel: {
      title: text("Request channel"),
      dataIndex: "requestChannel",
      key: "channel",
      width: USE_PERSON_REST_PERIODS_CONTROLLER_COLUMN_WIDTH.channel,
      sortValue: (row) => {
        const key = contractRequestChannelLabelKey(row.requestChannel);
        return key ? text(key) : "";
      },
      render: (_, row) => {
        const key = contractRequestChannelLabelKey(row.requestChannel);
        return key ? text(key) : "—";
      }
    },
    confirmation: {
      title: text("Confirmation"),
      dataIndex: "confirmationStatus",
      key: "confirmation",
      width: USE_PERSON_REST_PERIODS_CONTROLLER_COLUMN_WIDTH.confirmation,
      sortValue: (row) => row.isCancelled && row.confirmationStatus !== "sent"
        ? -1 : CONFIRMATION_SORT_ORDER[row.confirmationStatus],
      render: (_, row) => row.confirmationStatus === "sent"
        ? <Tag color="green">{text("Confirmation sent")}</Tag>
        : row.isCancelled ? "—"
        : row.confirmationStatus === "unknown"
          ? <Tag color="gold">{text("Confirmation unclear")}</Tag>
          : <Tag>{text("Confirmation open")}</Tag>
    },
    monthlyFee: {
      title: text("Fee"),
      dataIndex: "monthlyFee",
      key: "monthlyFee",
      width: USE_PERSON_REST_PERIODS_CONTROLLER_COLUMN_WIDTH.monthlyFee,
      sorter: (a, b) => {
        const aFee = a.isCancelled ? 0 : a.monthlyFee ?? 0;
        const bFee = b.isCancelled ? 0 : b.monthlyFee ?? 0;
        return aFee - bFee;
      },
      render: (_, row) => (!row.isCancelled && row.monthlyFee != null ? `${formatMoney(Number(row.monthlyFee || 0), units)} ${restPeriodFeeUnitText(restPeriodFeeAmountUnit(row), text)}` : "—")
    },
    weeksCount: {
      title: text("Installments"),
      dataIndex: "weeksCount",
      key: "weeksCount",
      width: USE_PERSON_REST_PERIODS_CONTROLLER_COLUMN_WIDTH.weeksCount,
      sorter: (a, b) => {
        const aCount = a.isCancelled ? 0 : a.weeksCount ?? 0;
        const bCount = b.isCancelled ? 0 : b.weeksCount ?? 0;
        return aCount - bCount;
      },
      render: (_, row) => (!row.isCancelled && row.weeksCount != null ? String(row.weeksCount) : "—")
    },
    options: {
      title: text("Options"),
      key: "options",
      width: USE_PERSON_REST_PERIODS_CONTROLLER_COLUMN_WIDTH.options,
      // Sortiert nach der Anzahl gesetzter Optionen — mehr Abweichung vom Standard zuerst.
      sortValue: (row) => row.isCancelled ? 0 : [
        row.extendsContract,
        row.continuePayment,
        row.billingScheduleMode === REST_PERIOD_BILLING_SCHEDULE_SHIFT_DUE_DATES
          || row.billingScheduleMode === REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME,
        row.appendFreeToEnd,
        row.allowCheckInDuringRestPeriod
      ].filter(Boolean).length,
      render: (_, row) => row.isCancelled ? "—" : (
        <Space wrap>
          {row.extendsContract ? <Tag color="blue">{text("time recovered")}</Tag> : <Tag>{text("no time recovery")}</Tag>}
          {row.continuePayment ? <Tag color="green">{text("continue payment")}</Tag> : <Tag>{text("payment paused")}</Tag>}
          {row.billingScheduleMode === REST_PERIOD_BILLING_SCHEDULE_SHIFT_DUE_DATES
            || row.billingScheduleMode === REST_PERIOD_BILLING_SCHEDULE_RECOVER_TIME
            ? <Tag color="purple">{text("Recover rest period time")}</Tag>
            : null}
          {row.appendFreeToEnd ? <Tag color="orange">{text("free at end")}</Tag> : null}
          {row.allowCheckInDuringRestPeriod ? (
            <Tag color="green">{text("Check-in allowed")}</Tag>
          ) : (
            <Tag>{text("check-in blocked")}</Tag>
          )}
        </Space>
      )
    }
  }), [text, units]);

  const actionsColumn = React.useMemo<ProColumnsWithMeasure<RestPeriodRow> | undefined>(
    () => canWriteRestPeriods
      ? {
          title: "",
          key: "actions",
          width: USE_PERSON_REST_PERIODS_CONTROLLER_COLUMN_WIDTH.actions,
          sorter: false,
          fixed: "right",
          align: "right",
          render: (_, row) => (
            <Space size="small">
              <Button type="link" size="small" icon={<EditOutlined />} disabled={row.isCancelled} onClick={() => openEdit(row)}>
                {text("Open")}
              </Button>
              <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => deleteRow(row)}>
                {text("Delete")}
              </Button>
            </Space>
          )
        }
      : undefined,
    [canWriteRestPeriods, deleteRow, openEdit, text]
  );

  const headerExtra = React.useMemo(
    () => canWriteRestPeriods
      ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {text("Add rest period")}
          </Button>
        )
      : undefined,
    [canWriteRestPeriods, openCreate, text]
  );

  const request = React.useCallback(async () => {
    const list = await queryClient.fetchQuery({
      queryKey: ["person", personId, "rest-periods"],
      queryFn: () => api.get<RestPeriodApiRow[]>(`/api/v1/rest-periods?personId=${encodeURIComponent(personId)}`),
      staleTime: 0
    });
    const rows = normalizeRestPeriodRows(list || []).sort(compareRestPeriodsNewestFirst);
    return { data: rows, success: true, total: rows.length };
  }, [api, personId, queryClient]);

  return {
    actionRef,
    open,
    edit,
    prefill,
    setOpen,
    setEdit,
    setPrefill,
    close,
    openCreate,
    openEdit,
    table: {
      headerExtra,
      columnDefinitions,
      allColumnsMap,
      actionsColumn,
      request,
      onRowClick: canWriteRestPeriods ? openEdit : undefined,
      pagination: { pageSize: REST_PERIODS_PAGE_SIZE }
    }
  };
}
